/**
 * Lớp gọi API. Một chỗ duy nhất giữ token và dịch lỗi sang tiếng Việt.
 *
 * **Token nằm trong `localStorage`.** Đây là dashboard một người dùng, chạy sau nginx trên máy
 * riêng — không phải một SaaS công cộng. Đánh đổi đã biết: XSS đọc được token. Bù lại bằng việc
 * **không có ô nhập nào render HTML thô** ở phía dashboard.
 */

const KHOA_TOKEN = "autoime.token";

export function docToken(): string | null {
  try {
    return localStorage.getItem(KHOA_TOKEN);
  } catch {
    return null;
  }
}

export function luuToken(token: string): void {
  try {
    localStorage.setItem(KHOA_TOKEN, token);
  } catch {
    /* trình duyệt chặn lưu — vẫn dùng được trong phiên này */
  }
}

export function xoaToken(): void {
  try {
    localStorage.removeItem(KHOA_TOKEN);
  } catch {
    /* bỏ qua */
  }
}

export class LoiApi extends Error {
  readonly ma: number;

  constructor(ma: number, thongDiep: string) {
    super(thongDiep);
    this.name = "LoiApi";
    this.ma = ma;
  }
}

async function goi<T>(duong: string, tuyChon: RequestInit = {}): Promise<T> {
  const token = docToken();
  const headers: Record<string, string> = { ...(tuyChon.headers as Record<string, string>) };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (tuyChon.body) headers["Content-Type"] = "application/json";

  let phanHoi: Response;
  try {
    phanHoi = await fetch(`/api${duong}`, { ...tuyChon, headers });
  } catch {
    // Phân biệt "máy chủ chưa chạy" với "máy chủ trả lỗi" — hai chuyện cần hai cách xử lý.
    throw new LoiApi(0, "Không nối được máy chủ AutoIME. Nó đã chạy chưa?");
  }

  if (phanHoi.status === 401) {
    xoaToken();
    throw new LoiApi(401, "Phiên đã hết hạn. Đăng nhập lại.");
  }

  let than: unknown = null;
  try {
    than = await phanHoi.json();
  } catch {
    /* không phải JSON */
  }

  if (!phanHoi.ok) {
    const t = than as { message?: string | string[] } | null;
    const td = Array.isArray(t?.message) ? t.message.join(" · ") : (t?.message ?? `Lỗi ${phanHoi.status}`);
    throw new LoiApi(phanHoi.status, td);
  }

  return than as T;
}

/* ── Kiểu dữ liệu ─────────────────────────────────────────────────────────── */

export type TrangThaiBai =
  | "BRIEF_READY" | "WRITING" | "NEEDS_IMAGE" | "PENDING_REVIEW"
  | "REJECTED_TEXT" | "REJECTED_IMAGE" | "APPROVED" | "SCHEDULED"
  | "PUBLISHED_WEB" | "PUBLISHED_ALL" | "FAILED" | "DISCARDED";

export interface SongNgu {
  vi: string;
  en?: string;
}

export interface Bai {
  _id: string;
  siteSlug: string;
  title: SongNgu;
  contentHtml: SongNgu;
  slug: string;
  captionFacebook: string | null;
  trangThai: TrangThaiBai;
  lyDoTuChoi: string | null;
  theTuChoi: string[];
  soLanVietLai: number;
  soLanVeLaiAnh: number;
  modelVietBai: string | null;
  tuKhoaMucTieu: string[];
  gioDangDuKien: string | null;
  dangFacebook: boolean;
  /** Bài do `seed-mau` tạo, **không phải bài thật**. Xem `checklist.md` **B8**. */
  laDuLieuMau: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TinCanhBao {
  muc: "tin" | "canh-bao" | "nghiem-trong";
  noiDung: string;
  nguon: string;
  luc: string;
}

export interface TongQuan {
  demTheoTrangThai: Record<TrangThaiBai, number>;
  tongBai: number;
  chuaCoBaiNao: boolean;
  chiPhi: { ngayUsd: number; thangUsd: number };
  canhBaoGanDay: TinCanhBao[];
  soCanhBaoNghiemTrong: number;
  /** Cron tự chạy job hay không. Đọc từ Cài đặt, KHÔNG ghi cứng ở giao diện. */
  cronBat: boolean;
  /**
   * Bao nhiêu bài trong các bộ đếm trên là **dữ liệu mẫu**.
   *
   * Không lọc khỏi bộ đếm — lọc thì tổng không khớp bảng, và việc đã lọc là thứ người đọc không
   * nhìn thấy. Một con số đứng cạnh thì nhìn thấy được.
   */
  soBaiMau: number;
}

export interface KetQuaDuyet {
  articleId: string;
  trangThaiMoi: TrangThaiBai;
  soJobDaHuy: number;
  jobTiepTheo: string | null;
  canhBao: string[];
}

export interface Luat {
  _id: string;
  noiDung: string;
  ápDungCho: string;
  siteSlug: string | null;
  ghim: boolean;
  dangBat: boolean;
  nguoiGhi: string;
  tags: string[];
  sinhTuReviewActionId: string | null;
  createdAt: string;
  /** `null` = không truy được lý do gốc — **khác** với "không có lý do". */
  lyDoGoc: string | null;
}

export interface ChiPhiTongQuan {
  ngayUsd: number;
  thangUsd: number;
  tranNgayUsd: number;
  tranNgayDaDat: boolean;
  tranThangUsd: number;
  tranThangDaDat: boolean;
  conLaiNgayUsd: number;
  conLaiThangUsd: number;
  /** 0 ⇒ **chưa đo được**, không phải "đã tiêu $0". */
  soDongSoCai: number;
  soDongLoi: number;
  dauNgayVN: string;
  dauThangVN: string;
}

export interface DongSoCai {
  _id: string;
  catalogKey: string;
  tacVu: string;
  siteSlug: string | null;
  articleId: string | null;
  tokenVao: number;
  tokenRa: number;
  tokenThinking: number;
  tienUsd: number;
  thanhCong: boolean;
  loi: string | null;
  giayChay: number;
  luc: string;
}

export interface MucCaiDat {
  khoa: string;
  kieu: "bool" | "so" | "chuoi" | "model" | "tiLe";
  moTa: string;
  giaTri: unknown;
  /** Lynust phải tự chọn — máy vẫn chạy khi trống, nhưng màn hình phải nói ra. */
  phaiChon: boolean;
  /** `false` = máy đang dùng mặc định, **chưa ai chọn**. Hai chuyện khác nhau. */
  daDat: boolean;
  nguoiSua: string | null;
  toiThieu: number | null;
  toiDa: number | null;
  modelChoPhep:
    | {
        catalogKey: string;
        nhaCungCap: string;
        nhom: string;
        cu: boolean;
        giaVaoUsdMoiTrieu: number;
        giaRaUsdMoiTrieu: number;
      }[]
    | null;
  modelMacDinh: string | null;
  /** Tổng model trong danh mục — so với `modelChoPhep.length` để biết có bị thu hẹp không. */
  tongSoModel: number | null;
}

export interface MucLich {
  _id: string;
  articleId: string;
  dich: string;
  gioDang: string;
  daChay: boolean;
  /** Giờ đã qua mà chưa chạy — bài **kẹt**, không phải bài sắp đăng. */
  quaHan: boolean;
  tieuDe: string | null;
  siteSlug: string | null;
  trangThaiBai: string | null;
  cheDoThucTe: string;
  ketQua: {
    thanhCong: boolean;
    cheDo: string;
    imeSlug: string | null;
    fbPostId: string | null;
    fbCommentId: string | null;
    loi: string | null;
    luc: string;
  } | null;
}

export interface LichTraVe {
  muc: MucLich[];
  /** Bài `APPROVED` mà chưa có dòng lịch nào — trạng thái xác sống, cần bấm "Xếp lịch". */
  chuaXepLich: { articleId: string; tieuDe: string | null; siteSlug: string | null }[];
  sapDang: number;
  quaHan: number;
  daChay: number;
  publishMode: string;
  cronBat: boolean;
}

export interface HoSoSite {
  siteSlug: string;
  moTaChuDe: string;
  baiMoiKy: number;
  donViKy: string;
  batBuocSongNgu: boolean;
  /** `false` = chưa ai đặt hồ sơ ⇒ `taoBriefCatalogue()` sẽ NÉM khi tới lượt site này. */
  daCoHoSo: boolean;
  soBaiDaViet: number;
}

/* ── Cấu trúc prompt ─────────────────────────────────────────────────────── */

export interface NguonPromptUI {
  khoa: string;
  nhan: string;
  moTa: string;
  tuDau: string;
  nhanTrongDeBai: string;
  tatDuoc: boolean;
  /** Nguồn CÓ ĐIỀU KIỆN — vắng mặt không phải lỗi. `null` = luôn có mặt. */
  chiKhi: string | null;
  /** `null` khi nguồn không tắt được ⇒ màn hình không dựng công tắc. */
  khoaCaiDat: string | null;
  dangBat: boolean;
}

export interface MucNguonUI {
  domain: string;
  tang: 1 | 2 | 3 | 4 | 5;
  ghiChu?: string;
  ngayThem: string;
  nguoiThem: string;
  /** `null`/thiếu = dùng cho MỌI site chạy Luồng 3. Mục khai trước 10/09/2026 không có trường này. */
  site?: string | null;
}

export interface KetQuaResearch {
  site: string;
  daChay: boolean;
  /** Vì sao không chạy. `null` khi đã chạy. */
  boQua: string | null;
  soChuDeGop: number;
  soNguonDocDuoc: number;
  soTrichDan: number;
  articleId: string | null;
  khongDuNguon: boolean;
}

export interface KetQuaQuetDomain {
  domain: string;
  site: string;
  duongFeed: string | null;
  soTinDocDuoc: number;
  soTinMoi: number;
  soChuDeTao: number;
  /** Còn tin mới vượt trần chưa lấy. Không có cờ này thì "mới 20" trông như đã hết. */
  daCat: boolean;
  loi: string | null;
}

export interface NguonTinCayTraVe {
  ds: MucNguonUI[];
  luong3ChayDuoc: boolean;
  /** Vì sao Luồng 3 dừng. "Chưa có nguồn" khác "không có việc" — hai câu, hai cách hiện. */
  lyDoDung: string | null;
  goiY: { domain: string; tang: 1 | 2 | 3 | 4 | 5; ghiChu: string }[];
  nhatKy: { luc: string; viec: "them" | "bo"; domain: string; tang: number | null; nguoi: string }[];
}

export interface CauTrucPromptTraVe {
  nguon: NguonPromptUI[];
  phongCach: {
    khoaTuDong: string;
    tuDong: boolean;
    dangXoay: string[];
    ds: { khoa: string; nhan: string; moTa: string; khoaCaiDat: string; dangXoay: boolean }[];
  };
  mucTieu: {
    khoaCaiDat: string;
    tong: number;
    tiLe: Record<string, number>;
    ds: { khoa: string; nhan: string; moTa: string }[];
  };
  nguong: {
    khoa: string;
    nhan: string;
    goiY: string;
    donVi: string;
    macDinh: number;
    toiThieu: number;
    toiDa: number;
    giaTri: number;
    daDat: boolean;
  }[];
}

export interface BanGhiPrompt {
  _id: string;
  tacVu: string;
  catalogKey: string;
  nguonDaDung: string[];
  soLan: number;
  /** NGUYÊN VĂN, không cắt. Cắt đi là bỏ mất đúng thứ khiến nó đáng lưu. */
  systemPrompt: string;
  userPrompt: string;
  luc: string;
}

export interface ChanDung {
  _id: string;
  ten: string;
  moTaNguoiViet: string;
  /** AI đúc kết từ GA4. Rỗng = **chưa đúc kết lần nào**, không phải "không có ai đọc". */
  moTaAiDucKet: string;
  siteSlug: string | null;
  lanDucKetCuoi: string | null;
  nguoiGhi: string;
}

export interface BaoCaoNguon {
  _id: string;
  siteSlug: string;
  noiDung: string;
  soTrichDan: number;
  /** Trích dẫn thiếu url / tên nguồn / ngày truy cập — Chốt G5 đòi đủ cả ba. */
  soTrichDanThieuTruong: number;
  trichDan: { url: string; tenNguon: string; ngayTruyCap: string; tang: number }[];
  dungDuocDeVietBai: boolean;
  dongGoogleSheet: string | null;
  luc: string;
}

export interface ModelDanhMuc {
  catalogKey: string;
  wireId: string;
  nhaCungCap: string;
  nhom: string;
  giaVaoUsdMoiTrieu: number;
  giaRaUsdMoiTrieu: number;
  cuaSoNguCanh: number;
  /** Model đời cũ — vẫn gọi được nhưng không nên chọn mới. */
  cu: boolean;
  moTa: string;
}

export interface VongXoay {
  bat: boolean;
  o: { catalogKey: string | null; bat: boolean }[];
  /** Chỉ những ô có model hợp lệ VÀ đang bật — đây là thứ thật sự được xoay qua. */
  dangBat: string[];
}

export interface KhoaApi {
  hang: "anthropic" | "openai" | "google";
  co: boolean;
  /** `db` = dán qua dashboard · `env` = từ .env · `khong` = chưa có. */
  nguon: "db" | "env" | "khong";
  /** Bốn ký tự CUỐI. Không bao giờ là khoá đầy đủ. */
  duoi: string | null;
}

export interface KetQuaKiem {
  luc: string;
  ok: boolean;
  chiTiet: string;
  loi: string | null;
}

export interface TruongKetNoiUI {
  khoa: string;
  nhan: string;
  biMat: boolean;
  batBuoc: boolean;
  goiY: string;
  nhieuDong: boolean;
  /** TEN bien moi truong, khong phai gia tri. */
  bienEnv: string | null;
  co: boolean;
  nguon: "db" | "env" | "khong";
  /** Bon ky tu CUOI. Khong bao gio la gia tri day du. */
  duoi: string | null;
  /** Truong KHONG bi mat thi hien thang — URL API va chat id can nhin thay de sua. */
  hienDuoc: string | null;
  /** May tu sinh, khong co o nhap. */
  tuSinh: boolean;
}

export interface HangKetNoiUI {
  khoa: string;
  ten: string;
  moTa: string;
  thieuThiSao: string;
  huongDan: string;
  kiemDuoc: boolean;
  /** Khac null = CHUA co dong code nao doc toi gia tri nay. */
  chuaDung: string | null;
  daDuCauHinh: boolean;
  /** Co it nhat mot truong da dat gia tri. */
  coGiaTriNao: boolean;
  truong: TruongKetNoiUI[];
  /** `null` = **chua kiem lan nao**, khac han "kiem roi va hong". */
  kiem: KetQuaKiem | null;
}

export interface KetNoiTraVe {
  hang: HangKetNoiUI[];
  /** Thieu ⇒ khong dan bi mat qua dashboard duoc, chi dung `.env`. */
  coKhoaMaHoa: boolean;
  publishMode: string;
  congTacSite: { siteSlug: string; bat: boolean }[];
  modelDangDung: { tacVu: string; catalogKey: string; nhaCungCap: string; tonTien: boolean }[];
}

export interface CaiDatTraVe {
  muc: MucCaiDat[];
  /** Chỉ đọc — nằm ở `.env`, đổi phải khởi động lại tiến trình. */
  publishMode: string;
  danhMucModel: ModelDanhMuc[];
  vongXoay: VongXoay;
  tacVuXoayVong: string;
  /** Tác vụ nào bắt buộc Anthropic, và VÌ SAO — câu trả lời đi kèm dữ liệu, không nằm trong docs. */
  rangBuocNhaCungCap: { tacVu: string; lyDo: string }[];
  soTacVu: number;
  sites: string[];
}

/* ── Lời gọi ──────────────────────────────────────────────────────────────── */

export interface TomTatSanPham {
  site: string;
  /** `false` cho `company` va `miradry` — dung thiet ke ben Website IME, khong phai thieu sot. */
  coCatalogue: boolean;
  tong: number;
  conBenIme: number;
  ngungDung: number;
  anhDangDuoc: number;
  anhKhongDangDuoc: number;
  /** `null` = **chua dong bo lan nao**. Khac han "dong bo roi, 0 thay doi". */
  dongBoLuc: string | null;
}

export interface MucSanPham {
  id: string;
  tenSanPham: string;
  idBenIme: string | null;
  soAnh: number;
  anhDangDuoc: number;
  /** Anh dau tien DANG DUOC — khong phai anh dau tien. Dashboard hien dung tam bai se dung. */
  anhDaiDien: string | null;
  conBenIme: boolean;
  ngungDungLuc: string | null;
  lanCuoiDungLuc: string | null;
  soLanDaDung: number;
}

export interface KetQuaDongBoSite {
  site: string;
  coCatalogue: boolean;
  /** `false` ⇒ moi so ben duoi la **chua do duoc**, khong phai 0. */
  docDuoc: boolean;
  loi: string | null;
  canLam: string | null;
  tuIme: number;
  moi: number;
  capNhat: number;
  ngungDung: number;
  songLai: number;
  anhDangDuoc: number;
  anhKhongDangDuoc: number;
  viDuAnhHong: string | null;
}

export interface KetQuaDongBo {
  giay: number;
  site: KetQuaDongBoSite[];
}

export const api = {
  dangNhap: (email: string, matKhau: string) =>
    goi<{ token: string; nguoiDung: { id: string; email: string; ten: string } }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, matKhau }),
    }),

  toi: () => goi<{ id: string; email: string; ten: string }>("/auth/toi"),

  health: () =>
    goi<{ khoe: boolean; db: string; cheDoDang: string; chuaCoJobNao: boolean }>("/health"),

  tongQuan: () => goi<TongQuan>("/tong-quan"),

  hangCho: () => goi<Bai[]>("/hang-cho"),

  /** Một bài theo id, **bất kể trạng thái** — bài đã duyệt hay đã đăng vẫn mở được. */
  motBai: (id: string) => goi<Bai>(`/bai-viet/${id}`),

  duyet: (id: string) => goi<KetQuaDuyet>(`/bai-viet/${id}/duyet`, { method: "POST" }),

  tuChoiBai: (id: string, lyDo: string, the: string[]) =>
    goi<KetQuaDuyet>(`/bai-viet/${id}/tu-choi-bai`, {
      method: "POST",
      body: JSON.stringify({ lyDo, the }),
    }),

  tuChoiAnh: (id: string, lyDo: string, the: string[]) =>
    goi<KetQuaDuyet>(`/bai-viet/${id}/tu-choi-anh`, {
      method: "POST",
      body: JSON.stringify({ lyDo, the }),
    }),

  bo: (id: string, lyDo: string) =>
    goi<KetQuaDuyet>(`/bai-viet/${id}/bo`, { method: "POST", body: JSON.stringify({ lyDo }) }),

  chayMotLuot: () =>
    goi<{ daChay: number; conViec: boolean; ghiChu: string }>("/job/chay-mot-luot", {
      method: "POST",
    }),

  aiDangDung: () =>
    goi<Record<string, { catalogKey: string; nhaCungCap: string; tonTien: boolean }>>(
      "/ai/dang-dung",
    ),

  /* ── Eval so sánh model ── */

  evalModels: () =>
    goi<
      {
        catalogKey: string;
        nhaCungCap: string;
        giaVaoUsdMoiTrieu: number;
        giaRaUsdMoiTrieu: number;
        moTa: string;
      }[]
    >("/eval/model-co-the-chon"),

  evalUocTien: (boDe: string, models: string[]) =>
    goi<{
      soDe: number;
      soLoiGoi: number;
      uocUsd: number;
      theoModel: { catalogKey: string; uocUsd: number }[];
    }>(`/eval/uoc-tien?boDe=${encodeURIComponent(boDe)}&models=${encodeURIComponent(models.join(","))}`),

  evalChay: (boDe: string, luot: string, models: string[]) =>
    goi<{ daChay: number; thanhCong: number; tienUsd: number }>("/eval/chay", {
      method: "POST",
      body: JSON.stringify({ boDe, luot, models }),
    }),

  evalBangXepHang: (boDe: string, luot: string) =>
    goi<
      {
        _id: string;
        soBai: number;
        soThanhCong: number;
        diemMayTb: number | null;
        diemTuNhienTb: number | null;
        diemChuyenMonTb: number | null;
        diemDungDuocTb: number | null;
        soDaChamNguoi: number;
        tienUsd: number;
      }[]
    >(`/eval/bang-xep-hang?boDe=${encodeURIComponent(boDe)}&luot=${encodeURIComponent(luot)}`),

  /** ⚠️ Cố ý KHÔNG có `catalogKey` và `diemMay` — chấm phải mù. */
  evalChamMu: (boDe: string, luot: string) =>
    goi<
      {
        id: string;
        siteSlug: string;
        title: { vi?: string };
        contentHtml: { vi?: string };
        captionFacebook: string | null;
      }[]
    >(`/eval/cham-mu?boDe=${encodeURIComponent(boDe)}&luot=${encodeURIComponent(luot)}`),

  evalCham: (
    id: string,
    diem: { tuNhien: number; chuyenMon: number; dungDuocNgay: number; nhanXet?: string },
  ) => goi<{ ok: true }>(`/eval/cham-mu/${id}`, { method: "POST", body: JSON.stringify(diem) }),

  /* ── Bài học (vòng học) ── */

  baiHoc: (loc: { apDungCho?: string; site?: string; chiDangBat?: boolean } = {}) => {
    const p = new URLSearchParams();
    if (loc.apDungCho) p.set("apDungCho", loc.apDungCho);
    if (loc.site) p.set("site", loc.site);
    if (loc.chiDangBat) p.set("chiDangBat", "1");
    const q = p.toString();
    return goi<{ luat: Luat[]; soTuChoiChuaSinhLuat: number }>(`/bai-hoc${q ? `?${q}` : ""}`);
  },

  baiHocGhim: (id: string, bat: boolean) =>
    goi<{ ok: true }>(`/bai-hoc/${id}/ghim`, { method: "POST", body: JSON.stringify({ bat }) }),

  baiHocBat: (id: string, bat: boolean) =>
    goi<{ ok: true }>(`/bai-hoc/${id}/bat`, { method: "POST", body: JSON.stringify({ bat }) }),

  baiHocSua: (id: string, noiDung: string) =>
    goi<{ ok: true }>(`/bai-hoc/${id}/sua`, { method: "POST", body: JSON.stringify({ noiDung }) }),

  baiHocChayBu: (toiDa = 20) =>
    goi<{ daXep: number; conLai: number }>("/bai-hoc/chay-bu", {
      method: "POST",
      body: JSON.stringify({ toiDa }),
    }),

  /* ── Chi phí AI ── */

  chiPhiTongQuan: () => goi<ChiPhiTongQuan>("/chi-phi/tong-quan"),

  chiPhiTheoTacVu: () =>
    goi<{ tacVu: string; soLoiGoi: number; soLoi: number; tienUsd: number }[]>("/chi-phi/theo-tac-vu"),

  chiPhiSoCai: (chiLoi = false, gioiHan = 100) =>
    goi<DongSoCai[]>(`/chi-phi/so-cai?gioiHan=${gioiHan}${chiLoi ? "&chiLoi=1" : ""}`),

  /* ── Lịch đăng ── */

  lich: (gioiHan = 100) => goi<LichTraVe>(`/lich?gioiHan=${gioiHan}`),

  lichXep: (articleId: string) =>
    goi<{ articleId: string; gioWeb: string; gioFb: string | null }>(`/lich/xep/${articleId}`, {
      method: "POST",
    }),

  lichDangNgay: (id: string) =>
    goi<{ daChay: boolean; ghiChu: string; trangThaiJob: string | null; loi: string | null }>(
      `/lich/dang-ngay/${id}`,
      { method: "POST" },
    ),

  /* ── Cài đặt ── */

  caiDat: () => goi<CaiDatTraVe>("/cai-dat"),

  /**
   * `canhBao` là những thứ **đúng hôm nay, sai tháng sau** — nhóm Telegram thường sắp bị nâng cấp
   * (chat id sẽ đổi), trần chi phí cao hơn mọi lần nạp từ trước tới nay. Lưu vẫn thành công.
   * Ném thì chặn người dùng làm việc đúng; nuốt thì mất luôn thông tin.
   */
  datCaiDat: (khoa: string, giaTri: unknown) =>
    goi<{ khoa: string; giaTri: unknown; canhBao: string[] }>("/cai-dat", {
      method: "PUT",
      body: JSON.stringify({ khoa, giaTri }),
    }),

  /* ── Cấu trúc prompt ── */

  hoSoSite: () => goi<HoSoSite[]>("/prompt/ho-so-site"),
  cauTrucPrompt: () => goi<CauTrucPromptTraVe>("/prompt/cau-truc"),

  suaHoSoSite: (site: string, than: { moTaChuDe: string; baiMoiKy?: number; donViKy?: string }) =>
    goi<{ siteSlug: string; moTaChuDe: string; baiMoiKy: number; donViKy: string }>(
      `/prompt/ho-so-site/${site}`,
      { method: "POST", body: JSON.stringify(than) },
    ),

  banGhiPrompt: () =>
    goi<{
      bai: { articleId: string; tieuDe: string | null; siteSlug: string | null; soBanGhi: number; tacVu: string[] }[];
      soBaiKhongCoBanGhi: number;
    }>("/prompt/ban-ghi"),

  banGhiCuaBai: (articleId: string) =>
    goi<{ tieuDe: string | null; siteSlug: string | null; banGhi: BanGhiPrompt[] }>(
      `/prompt/ban-ghi/${articleId}`,
    ),

  /* ── Chân dung khách ── */

  chanDung: () => goi<{ ds: ChanDung[]; sites: string[] }>("/chan-dung"),

  themChanDung: (than: { ten: string; moTaNguoiViet: string; siteSlug?: string | null }) =>
    goi<{ _id: string }>("/chan-dung", { method: "POST", body: JSON.stringify(than) }),

  suaChanDung: (id: string, than: { ten: string; moTaNguoiViet: string; siteSlug?: string | null }) =>
    goi<{ ok: true }>(`/chan-dung/${id}`, { method: "POST", body: JSON.stringify(than) }),

  /* ── Nguồn & báo cáo ── */

  nguon: () => goi<{ ds: BaoCaoNguon[]; tong: number }>("/nguon"),

  /* ── Website được phép làm nguồn (Luồng 3) ── */

  nguonTinCay: () => goi<NguonTinCayTraVe>("/nguon-tin-cay"),

  nguonTinCayThem: (domain: string, tang: number, ghiChu: string, site: string | null) =>
    goi<{ loai: "them" | "da-co" | "khong-hop-le"; domain: string | null; lyDo: string | null }>(
      "/nguon-tin-cay",
      { method: "POST", body: JSON.stringify({ domain, tang, ghiChu, site }) },
    ),

  /** ⚠️ **TỐN TIỀN.** Một lượt tổng hợp cho cả hai site nghiên cứu, bỏ qua nhịp 3 ngày. */
  researchChay: () => goi<{ ds: KetQuaResearch[] }>("/research/chay", { method: "POST" }),

  /** Quét tin mới trên mọi website đã khai. **Không tốn tiền model** — chỉ tải feed. */
  docTinQuet: () =>
    goi<{ ds: KetQuaQuetDomain[]; tongChuDeTao: number; boQua: string | null }>("/doc-tin/quet", {
      method: "POST",
    }),

  /** `encodeURIComponent`: domain đi trong đường dẫn, và người ta dán cả URL đầy đủ. */
  nguonTinCayBo: (domain: string) =>
    goi<{ daBo: boolean; domain: string | null; lyDo: string | null }>(
      `/nguon-tin-cay/${encodeURIComponent(domain)}`,
      { method: "DELETE" },
    ),

  /* ── Kết nối ── */

  ketNoi: () => goi<KetNoiTraVe>("/ket-noi"),

  /** Luu nhieu truong cua mot duong. O de trong bi BO QUA, khong bi coi la "xoa". */
  ketNoiLuu: (hang: string, giaTri: Record<string, string>) =>
    goi<{ daLuu: string[]; kiem: KetQuaKiem | null; buocTiep: string | null }>(`/ket-noi/${hang}`, {
      method: "POST",
      body: JSON.stringify({ giaTri }),
    }),

  ketNoiKiem: (hang: string) =>
    goi<KetQuaKiem>(`/ket-noi/${hang}/kiem`, { method: "POST" }),

  ketNoiXoaTruong: (hang: string, truong: string) =>
    goi<{ ok: true }>(`/ket-noi/${hang}/truong/${encodeURIComponent(truong)}`, {
      method: "DELETE",
    }),

  /** Facebook buoc 1 — doi token ngan lay danh sach trang kem Page token dai han. */
  fbDoiToken: (tokenNgan: string) =>
    goi<{ trang: { id: string; ten: string; token: string }[] }>("/ket-noi/facebook/doi-token", {
      method: "POST",
      body: JSON.stringify({ tokenNgan }),
    }),

  /** Facebook buoc 2 — chot trang, luu Page token, kiem ngay. */
  fbChonTrang: (pageId: string, pageToken: string) =>
    goi<KetQuaKiem>("/ket-noi/facebook/chon-trang", {
      method: "POST",
      body: JSON.stringify({ pageId, pageToken }),
    }),

  sanPhamTomTat: () => goi<{ site: TomTatSanPham[] }>("/catalogue"),

  sanPhamCuaSite: (site: string) =>
    goi<{ site: string; muc: MucSanPham[] }>(`/catalogue/${site}`),

  /** `site` bo trong = dong bo tat ca. Dang chay thi may chu tra **409**. */
  dongBoCatalogue: (site?: string) =>
    goi<KetQuaDongBo>("/catalogue/dong-bo", {
      method: "POST",
      body: JSON.stringify(site ? { site } : {}),
    }),
};
